<script lang="ts" module>
	export const SuccessMessage =
		'You successfully subscribed to our newsletter.';
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { twMerge } from 'tailwind-merge';
	import {
		NewsletterMixpanelEvents,
		NewsletterStartTrigger,
	} from '../../constants/mixpanel-events';
	import { createNewsletterFunnel } from '../../runes/createNewsletterFunnel/createNewsletterFunnel.svelte';
	import { NewsletterFormHelper } from './NewsletterFormHelper';
	import { Config } from '#lib/constants/config.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { NewsletterValidationSchema } from '#lib/features/newsletter/constants/newsletter-validation-schema.js';
	import { createNewsletterMutation } from '#lib/features/newsletter/utils/createNewsletterMutation/index.js';
	import { createNewsletterRecaptchaMutation } from '#lib/features/newsletter/utils/createNewsletterRecaptchaMutation/createNewsletterRecaptchaMutation.js';
	import { isOnProductionOnly } from '#lib/utils/env/isOnProductionOnly/isOnProductionOnly.js';
	import { Button } from '$global/components/Button';
	import { ButtonInput } from '$global/components/ButtonInput';
	import type { InputProps } from '$global/components/Input';
	import { Input } from '$global/components/Input';
	import type { InputClasses } from '$global/components/Input/Input.svelte';
	import { Txt } from '$global/components/Txt';
	import { GenericErrorMessage } from '$global/constants/generic-error-message';
	import { createForm } from '$global/utils/form-utils/form-utils.svelte';
	import { identifyUser } from '$global/utils/identifyUser';
	import {
		AuthAction,
		AuthMethod,
		AuthProvenance,
		trackAuthEvent,
	} from '$global/utils/trackAuthEvent';
	import { trackHashed } from '$global/utils/trackHashed/trackHashed';

	let {
		isHeniNewsletter,
		recaptcha = undefined,
		listId,
		variant = 'dark',
		label = undefined,
		fullWidthInput = false,
		showSeparateFieldsOnMobile = false,
		showSeparateFields = false,
		buttonText = 'subscribe',
		inputPropsEmail = {},
		classes = {},
		privacyHref = GlobalRoutes.PrivacyUk,
		captureName = false,
		provenance,
		startTrigger = NewsletterStartTrigger.Interaction,
		onSuccess,
		onError,
		helper: helperSnippet,
		showPrivacyPolicy = true,
		...rest
	}: Props = $props();

	interface Props {
		isHeniNewsletter: boolean;
		onSuccess?: () => void;
		/**
		 * Receives the message the form shows inline, so callers that hide the
		 * inline error (e.g. toast-based widgets) can surface the same text.
		 */
		onError?: (message: string) => void;
		class?: string;
		recaptcha?: boolean | undefined;
		listId: number | number[];
		variant?: 'dark' | 'light';
		label?: string | undefined;
		fullWidthInput?: boolean;
		showSeparateFields?: boolean;
		showSeparateFieldsOnMobile?: boolean;
		buttonText?: string;
		inputPropsEmail?: Partial<InputProps>;
		classes?: {
			buttonInput?: string;
			buttonInputClasses?: InputClasses;
			subscribeHelperText?: string;
			nameContainer?: string;
			emailContainer?: string;
			firstNameContainer?: string;
			successMessage?: string;
		};
		helper?: import('svelte').Snippet;
		privacyHref?: string;
		captureName?: boolean;
		provenance: string;
		/**
		 * When `started` is emitted. Defaults to `Interaction` so only forms that
		 * are deliberate on show — dialogs, dedicated pages — opt into `Mount`.
		 */
		startTrigger?: NewsletterStartTrigger;
		showPrivacyPolicy?: boolean;
	}

	const subscribeToNewsletterRecaptcha = createNewsletterRecaptchaMutation();
	const subscribeToNewsletter = createNewsletterMutation();
	const dataCyPrefix = 'footer-newsletter-form';

	const funnel = createNewsletterFunnel({
		provenance: () => provenance,
		startTrigger: () => startTrigger,
		newsletterListId: () => listId,
		email: () => form.email.value,
	});

	let errorMessage = $state('');
	let successMessage = $state('');

	const INITIAL_FORM_VALUES = {
		email: '',
		firstname: '',
		surname: '',
	};

	let { form, handleBlur, setBlur, resetForm, getFieldErrorMessage } =
		createForm({
			schema: NewsletterValidationSchema,
			initialValues: INITIAL_FORM_VALUES,
			trackingName: 'newsletter',
		});

	$effect(() => {
		if (successMessage) {
			setTimeout(() => {
				successMessage = '';
			}, 3000);
		}
	});

	const handleChange = () => {
		errorMessage = '';
		successMessage = '';
	};

	const handleInput = () => {
		funnel.onInteraction();
	};

	const subscribe = async (newsletterListId: number) => {
		errorMessage = '';
		successMessage = '';

		const payload = {
			listId: newsletterListId,
			isHeniNewsletter,
			email: form.email.value,
			...{
				...(captureName &&
					form.firstname.value && { first_name: form.firstname.value }),
				...(captureName &&
					form.surname.value && { last_name: form.surname.value }),
			},
		};

		const callbacks = {
			onSuccess: async () => {
				funnel.onSubmitSuccess();

				void trackHashed(
					NewsletterMixpanelEvents.SubmitNewsletter,
					{ email: form.email.value },
					{ newsletterListId, provenance }
				);

				if (
					isOnProductionOnly() &&
					window?.fbq &&
					newsletterListId === Config.NewsletterListIds.News
				) {
					window.fbq('trackCustom', 'news-subscription', {
						user_email: form.email.value,
					});
				}

				await identifyUser({
					email: form.email.value,
					firstName: form.firstname.value || undefined,
					lastName: form.surname.value || undefined,
				});
				trackAuthEvent({
					action: AuthAction.Identified,
					method: AuthMethod.Email,
					provenance: AuthProvenance.Newsletter,
					placement: provenance,
				});

				resetForm();

				if (onSuccess) {
					onSuccess();
				}

				successMessage = SuccessMessage;
			},
			onError: (error: unknown) => {
				funnel.onSubmitError(error);

				errorMessage = GenericErrorMessage;
				if (onError) {
					onError(GenericErrorMessage);
				}
			},
		};

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if (recaptcha && (window as any).grecaptcha) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(window as any).grecaptcha.ready(async () => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const recaptchaToken = await (window as any).grecaptcha.execute(
					Config.RecaptchaKey,
					{
						action: 'validate_captcha',
					}
				);

				await subscribeToNewsletterRecaptcha.mutate(
					{ ...payload, recaptcha_token: recaptchaToken },
					callbacks
				);
			});
		} else {
			await subscribeToNewsletter.mutate(payload, callbacks);
		}
	};

	const handleSubmit = async (e: Event) => {
		e.preventDefault();

		// The button-input variant never disables its submit button, so an empty
		// or malformed email would otherwise reach the API and come back as a
		// generic failure. Surface the field's own validation message instead.
		if (form.email.error) {
			setBlur('email', true);
			if (onError) {
				onError(form.email.error);
			}
			return;
		}

		if (Array.isArray(listId)) {
			listId.map(subscribe);
		} else {
			subscribe(listId);
		}
	};
</script>

<form action="javascript:void(0);" onsubmit={handleSubmit} {...rest}>
	{#if captureName}
		<div
			class={twMerge(
				'mb-3 block justify-between sm:grid sm:grid-cols-2 sm:gap-2',
				classes?.nameContainer
			)}
		>
			<div
				class={twMerge(
					'mb-2 flex flex-col sm:mb-0',
					classes?.firstNameContainer
				)}
			>
				<Input
					label="First Name"
					placeholder="First Name"
					name="firstname"
					dataCy={dataCyPrefix}
					required={recaptcha}
					bind:value={form.firstname.value}
					onblur={handleBlur('firstname')}
					oninput={handleInput}
					error={recaptcha && form.firstname.blur && !form.firstname.value
						? 'Please provide your first name'
						: ''}
				/>
			</div>
			<div class="flex flex-col">
				<Input
					label="Surname"
					placeholder="Last Name"
					name="surname"
					dataCy={dataCyPrefix}
					required={recaptcha}
					bind:value={form.surname.value}
					onblur={handleBlur('surname')}
					oninput={handleInput}
					error={recaptcha && form.surname.blur && !form.surname.value
						? 'Please provide your surname'
						: ''}
				/>
			</div>
		</div>
	{/if}
	{#if showSeparateFieldsOnMobile || showSeparateFields}
		<div
			class={classNames(`flex flex-col`, {
				'md:hidden': showSeparateFieldsOnMobile,
			})}
		>
			<div class={twMerge('mb-3', classes?.emailContainer)}>
				<Input
					placeholder="user@example.com"
					name="email"
					dataCy={dataCyPrefix}
					success={!errorMessage && successMessage ? successMessage : undefined}
					required={false}
					classes={{ success: classes?.successMessage }}
					bind:value={form.email.value}
					onblur={handleBlur('email')}
					onchange={handleChange}
					oninput={handleInput}
					error={getFieldErrorMessage('email') || errorMessage}
					{...inputPropsEmail}
				/>
			</div>

			<Button
				disabled={recaptcha
					? !!form.email.error ||
						(captureName && !form.firstname.value) ||
						(captureName && !form.surname.value)
					: !!form.email.error}
				loading={subscribeToNewsletterRecaptcha.isPending ||
					subscribeToNewsletter.isPending}
				fullWidth
				dataCy={dataCyPrefix}
				size="md">SUBSCRIBE</Button
			>

			{#if showPrivacyPolicy}
				<Txt
					variant="body3"
					class={twMerge('mt-3 text-gray-500', classes?.subscribeHelperText)}
				>
					<NewsletterFormHelper href={privacyHref} helper={helperSnippet} />
				</Txt>
			{/if}
		</div>
	{/if}

	<div
		class={classNames({
			'hidden md:block': showSeparateFieldsOnMobile,
			hidden: showSeparateFields,
		})}
	>
		<ButtonInput
			dataCy={`${dataCyPrefix}-subscribe`}
			inputVariant={variant}
			buttonVariant={variant === 'dark' ? 'light' : 'dark'}
			{label}
			placeholder="user@example.com"
			name="email"
			loading={recaptcha
				? subscribeToNewsletterRecaptcha.isPending
				: subscribeToNewsletter.isPending}
			bind:value={form.email.value}
			onblur={handleBlur('email')}
			onchange={handleChange}
			oninput={handleInput}
			required={false}
			classes={{ buttonInputClasses: classes.buttonInputClasses }}
			error={getFieldErrorMessage('email') || errorMessage}
			success={successMessage}
			class={classNames({
				'w-full': fullWidthInput,
			})}
		>
			{buttonText}
		</ButtonInput>

		<Txt
			variant="body3"
			class={twMerge('mt-4 text-gray-500', classes?.subscribeHelperText)}
		>
			<NewsletterFormHelper href={privacyHref} helper={helperSnippet} />
		</Txt>
	</div>
</form>
